import React from 'react';

interface NetworkIllustrationProps {
  className?: string;
}

/**
 * Decorative knowledge-network graph from the ENG-5288 design artifact.
 * Source: ./assets/network-graphic.svg (regeneration recipe in
 * .claude/plans/ENG-5288-simplify-network-card-plan.md, Task 3).
 * The export's hexes were exact light-mode matches of --brandPrimary (hubs)
 * and --onSurfaceSubtle (edges/dots), so colors are mapped to those theme
 * vars and the drawing adapts to dark mode. The mid-teal secondary nodes are
 * derived via color-mix, as in the artifact. Purely decorative — hidden from
 * assistive tech.
 */
export function NetworkIllustration({ className }: NetworkIllustrationProps): React.ReactNode {
  return (
    <svg viewBox="0 0 630 196" aria-hidden="true" className={className}>
      <line
        x1="96"
        y1="76"
        x2="124.3"
        y2="84.92"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="64.22"
        y2="101.55"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="32.29"
        y2="94.61"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="35.69"
        y2="76.56"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="142.06"
        y2="67.52"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="90.67"
        y2="101.54"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="95.34"
        y2="139.05"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="109.35"
        y2="134.21"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="109.35"
        y1="134.21"
        x2="106.27"
        y2="152.33"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="57.09"
        y2="67.74"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="92.83"
        y2="52.02"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="92.83"
        y1="52.02"
        x2="85.25"
        y2="38.99"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="106.77"
        y2="92.35"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="80.92"
        y2="29.07"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="118.3"
        y2="97.77"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="124.02"
        y2="95"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="79.78"
        y2="66.85"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="88.87"
        y2="103.32"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="107.11"
        y2="17.93"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="107.11"
        y1="17.93"
        x2="106.19"
        y2="11.85"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="119.49"
        y2="41.98"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="119.49"
        y1="41.98"
        x2="128.11"
        y2="32.8"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="78.74"
        y2="48.33"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="70.11"
        y2="43.8"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="288.54"
        y2="55.79"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="260.06"
        y2="10.52"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="247.33"
        y2="48.12"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="247.33"
        y1="48.12"
        x2="259.39"
        y2="42.24"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="247.39"
        y2="61.39"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="280.53"
        y2="50.42"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="193.59"
        y2="41.68"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="253.02"
        y2="5"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="241.84"
        y2="72.56"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="281.93"
        y2="71.74"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="281.93"
        y1="71.74"
        x2="287.99"
        y2="80.6"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="182.53"
        y2="40.26"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="244.33"
        y2="5"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="258.05"
        y2="5"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="233.95"
        y2="5"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="565.94"
        y2="160.93"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="468.25"
        y2="95.88"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="542.98"
        y2="133.83"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="546.47"
        y2="124.06"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="454.33"
        y2="158.92"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="454.33"
        y1="158.92"
        x2="448.34"
        y2="163.39"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="463.5"
        y2="176.81"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="524.87"
        y2="142.7"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="494.85"
        y2="165.32"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="517.92"
        y2="105.07"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="449.16"
        y2="127.67"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="532.82"
        y2="167.32"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="520.82"
        y2="87.15"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="483.95"
        y2="152.61"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="451.01"
        y2="150.55"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="447.9"
        y2="158.66"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="518.41"
        y2="92.99"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="484.81"
        y2="119.94"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="532.54"
        y2="138.75"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="480.69"
        y2="139.83"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="505"
        y1="150"
        x2="484.3"
        y2="123.54"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="174.07"
        y2="188.9"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="196.88"
        y2="139.29"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="129.81"
        y2="141.89"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="150.7"
        y2="152.4"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="187.31"
        y2="142.11"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="139.56"
        y2="168.42"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="198.38"
        y2="100.66"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="198.38"
        y1="100.66"
        x2="205.51"
        y2="85.62"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="164.22"
        y2="165.54"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="188.72"
        y2="128.36"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="234.7"
        y2="142.68"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="224.91"
        y2="187.72"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="224.91"
        y1="187.72"
        x2="236.29"
        y2="191"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="190.29"
        y2="167.26"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="231.19"
        y2="168.15"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="231.19"
        y1="168.15"
        x2="249.4"
        y2="173.54"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="188.43"
        y2="191"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="188.43"
        y1="191"
        x2="196.57"
        y2="191"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="176"
        y1="156"
        x2="200"
        y2="185.75"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="389.17"
        y2="82.4"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="327.15"
        y2="101.96"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="330.74"
        y2="113.46"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="393.49"
        y2="82.79"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="365.97"
        y2="152.79"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="365.97"
        y1="152.79"
        x2="361.39"
        y2="168.74"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="303.63"
        y2="111.14"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="359.25"
        y2="78.53"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="359.25"
        y1="78.53"
        x2="357.79"
        y2="64.84"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="373.85"
        y2="62.57"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="334.71"
        y2="113.25"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="309.22"
        y2="106.07"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="334.95"
        y2="87.09"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="334.95"
        y1="87.09"
        x2="320.65"
        y2="79.99"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="347.56"
        y2="143.74"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="347.56"
        y1="143.74"
        x2="349.38"
        y2="152.82"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="384.51"
        y2="95.53"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="367.33"
        y2="70.66"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="410.74"
        y2="128.59"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="346.44"
        y2="54.01"
        stroke="var(--onSurfaceSubtle)"
        strokeWidth="0.5"
        opacity="0.32"
      ></line>
      <line
        x1="232"
        y1="46"
        x2="216.77"
        y2="78.59"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="216.77"
        y1="78.59"
        x2="205.5"
        y2="107.14"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="205.5"
        y1="107.14"
        x2="183.69"
        y2="133.65"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="183.69"
        y1="133.65"
        x2="176"
        y2="156"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="360"
        y1="100"
        x2="387.86"
        y2="106.78"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="387.86"
        y1="106.78"
        x2="423.54"
        y2="119.36"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="423.54"
        y1="119.36"
        x2="461.67"
        y2="136.89"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="461.67"
        y1="136.89"
        x2="505"
        y2="150"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="160.89"
        y2="87.01"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="160.89"
        y1="87.01"
        x2="228.34"
        y2="89.45"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="228.34"
        y1="89.45"
        x2="287.25"
        y2="97.84"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="287.25"
        y1="97.84"
        x2="360"
        y2="100"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="96"
        y1="76"
        x2="117.7"
        y2="91.72"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="117.7"
        y1="91.72"
        x2="138.51"
        y2="123.85"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="138.51"
        y1="123.85"
        x2="151.78"
        y2="138.94"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <line
        x1="151.78"
        y1="138.94"
        x2="176"
        y2="156"
        stroke="var(--brandPrimary)"
        strokeWidth="0.8"
        opacity="0.32"
      ></line>
      <circle cx="96" cy="76" r="6.5" fill="var(--brandPrimary)" opacity="0.96"></circle>
      <circle cx="124.3" cy="84.92" r="1.48" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="64.22"
        cy="101.55"
        r="4.34"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="32.29"
        cy="94.61"
        r="2.87"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="35.69" cy="76.56" r="2.3" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="142.06" cy="67.52" r="1.4" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="90.67"
        cy="101.54"
        r="2.03"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="95.34" cy="139.05" r="1.71" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="109.35"
        cy="134.21"
        r="3.97"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="106.27" cy="152.33" r="2" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="57.09" cy="67.74" r="1.76" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="92.83" cy="52.02" r="1.58" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="85.25" cy="38.99" r="1.11" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="106.77" cy="92.35" r="1.76" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="80.92"
        cy="29.07"
        r="3.28"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="118.3" cy="97.77" r="1.35" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="124.02" cy="95" r="1.89" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="79.78" cy="66.85" r="2.06" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="88.87" cy="103.32" r="1.68" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="107.11" cy="17.93" r="2.2" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="106.19" cy="11.85" r="1.2" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="119.49"
        cy="41.98"
        r="4.26"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="128.11" cy="32.8" r="1.33" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="78.74" cy="48.33" r="1.73" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="70.11" cy="43.8" r="1.54" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="232" cy="46" r="4" fill="var(--brandPrimary)" opacity="0.96"></circle>
      <circle
        cx="288.54"
        cy="55.79"
        r="2.98"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="260.06" cy="10.52" r="2.09" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="247.33"
        cy="48.12"
        r="2.41"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="259.39" cy="42.24" r="2.07" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="247.39" cy="61.39" r="1.79" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="280.53" cy="50.42" r="2.15" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="193.59"
        cy="41.68"
        r="4.37"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="253.02"
        cy="5"
        r="2.2"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="241.84" cy="72.56" r="1.3" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="281.93"
        cy="71.74"
        r="3.3"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="287.99" cy="80.6" r="1.65" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="182.53" cy="40.26" r="1.28" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="244.33" cy="5" r="1.29" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="258.05" cy="5" r="1.49" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="233.95"
        cy="5"
        r="2.18"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="505" cy="150" r="6" fill="var(--brandPrimary)" opacity="0.96"></circle>
      <circle cx="565.94" cy="160.93" r="1.52" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="468.25" cy="95.88" r="1.76" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="542.98" cy="133.83" r="1.8" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="546.47" cy="124.06" r="1.8" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="454.33" cy="158.92" r="1.4" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="448.34" cy="163.39" r="1.3" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="463.5" cy="176.81" r="1.48" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="524.87" cy="142.7" r="2.28" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="494.85" cy="165.32" r="1.88" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="517.92"
        cy="105.07"
        r="4.28"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="449.16" cy="127.67" r="2.18" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="532.82"
        cy="167.32"
        r="3.21"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="520.82" cy="87.15" r="1.6" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="483.95"
        cy="152.61"
        r="2.73"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="451.01"
        cy="150.55"
        r="3.01"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="447.9" cy="158.66" r="1.24" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="518.41" cy="92.99" r="2.25" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="484.81"
        cy="119.94"
        r="2.67"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="532.54"
        cy="138.75"
        r="3.27"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="480.69" cy="139.83" r="1.51" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="484.3" cy="123.54" r="1.73" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="176" cy="156" r="4" fill="var(--brandPrimary)" opacity="0.96"></circle>
      <circle cx="174.07" cy="188.9" r="1.25" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="196.88"
        cy="139.29"
        r="3.91"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="129.81" cy="141.89" r="2.1" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="150.7"
        cy="152.4"
        r="3.88"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="187.31" cy="142.11" r="2.2" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="139.56" cy="168.42" r="1.83" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="198.38"
        cy="100.66"
        r="2.69"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="205.51" cy="85.62" r="2.02" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="164.22" cy="165.54" r="2.13" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="188.72" cy="128.36" r="1.76" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="234.7" cy="142.68" r="1.62" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="224.91" cy="187.72" r="2.13" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="236.29" cy="191" r="1.88" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="190.29" cy="167.26" r="1.61" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="231.19" cy="168.15" r="1.41" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="249.4" cy="173.54" r="1.15" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="188.43"
        cy="191"
        r="2.66"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="196.57" cy="191" r="1.87" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="200" cy="185.75" r="1.96" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="360" cy="100" r="5" fill="var(--brandPrimary)" opacity="0.96"></circle>
      <circle
        cx="389.17"
        cy="82.4"
        r="4.42"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="327.15"
        cy="101.96"
        r="2.22"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="330.74" cy="113.46" r="2.12" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="393.49"
        cy="82.79"
        r="3.88"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="365.97" cy="152.79" r="1.96" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="361.39" cy="168.74" r="1.65" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="303.63" cy="111.14" r="1.68" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="359.25"
        cy="78.53"
        r="2.15"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="357.79" cy="64.84" r="1.37" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="373.85"
        cy="62.57"
        r="2.39"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="334.71" cy="113.25" r="2.26" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="309.22" cy="106.07" r="1.84" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="334.95" cy="87.09" r="2.12" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="320.65" cy="79.99" r="1.04" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="347.56"
        cy="143.74"
        r="3.65"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="349.38" cy="152.82" r="1.16" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="384.51"
        cy="95.53"
        r="3.24"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle cx="367.33" cy="70.66" r="1.27" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle cx="410.74" cy="128.59" r="2.04" fill="var(--onSurfaceSubtle)" opacity="0.4"></circle>
      <circle
        cx="346.44"
        cy="54.01"
        r="2.39"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="216.77"
        cy="78.59"
        r="2.78"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="205.5"
        cy="107.14"
        r="2.2"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="183.69"
        cy="133.65"
        r="1.67"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="387.86"
        cy="106.78"
        r="1.94"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="423.54"
        cy="119.36"
        r="2.5"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="461.67"
        cy="136.89"
        r="3.2"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="160.89"
        cy="87.01"
        r="2.31"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="228.34"
        cy="89.45"
        r="2.33"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="287.25"
        cy="97.84"
        r="2"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="117.7"
        cy="91.72"
        r="2.64"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="138.51"
        cy="123.85"
        r="2.07"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
      <circle
        cx="151.78"
        cy="138.94"
        r="1.85"
        fill="color-mix(in oklab, var(--brandPrimary) 62%, var(--onSurfaceSubtle))"
        opacity="0.7"
      ></circle>
    </svg>
  );
}
